import { Component, ElementRef, HostListener, Input, OnInit } from '@angular/core';
import { BaseComponent } from 'src/app/core/base/base.component';
import { ActivatedRoute, Router } from '@angular/router';
import { subDays, startOfToday } from 'date-fns';
import { EMPTY, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map, switchMap, takeUntil } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SnotifyService } from 'ng-snotify';
import { RecommendationsService } from 'src/app/core/services/recommendation.service';
import { CompanyService } from 'src/app/core/services/company.service';
import { DateRangePeriod } from 'src/app/core/models/user';
import { BulkHistoryDetailModalComponent } from './bulk-history-detail-modal/bulk-history-detail-modal.component';
import Swal from 'sweetalert2';

const FILTER_STATUS = {
  ALL: 'ALL',
  COMPLETED: 'COMPLETED',
  IN_PROGRESS: 'IN_PROGRESS',
  PENDING: 'PENDING',
  FAILED: 'FAILED',
  APPLYING: 'APPLYING',
  REVERTED: 'REVERTED',
  REVERTING: 'REVERTING',
} as const;


export interface BulkJobHistory {
  jobBatchId: string;
  status: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  isRevert: boolean;
  isBeingReverted?: boolean;
  revertJobBatchId?: string;
  originalJobBatchId?: string;
  hasBeenReverted?: boolean;
  totalCount: number;
  appliedCount: number;
  doneCount: number;
  failedCount: number;
  invalidCount: number;
  triggeredByUserId?: string;
  triggeredBySystem?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  pendingRevert?: boolean;
  pendingRevertAt?: string;
  // Enriched client-side
  actorLabel?: string;
  increasesCount?: number;
  decreasesCount?: number;
}

export interface BulkJobSnapshot {
  jobBatchId: string;
  entityKey: string;
  entitySnapshot: { sku: string; asin: string; description?: string; imageUrl?: string };
  priceBefore: { inboundPrice: number; businessPrice?: number; salePrice?: number; minPrice?: number; maxPrice?: number };
  priceRequested?: { inboundPrice?: number; businessPrice?: number; salePrice?: number; minPrice?: number; maxPrice?: number };
  priceAfter?: { inboundPrice: number; businessPrice?: number; salePrice?: number; minPrice?: number; maxPrice?: number };
  outcome: 'PENDING' | 'APPLIED' | 'DONE' | 'FAILED' | 'INVALID';
  failureReason?: string;
}

@Component({
  selector: 'app-bulk-history',
  templateUrl: './bulk-history.component.html',
  styleUrls: ['./bulk-history.component.scss'],
})
export class BulkHistoryComponent extends BaseComponent implements OnInit {
  @Input() companyName = '';

  // Cancels any in-flight loadJobs / loadRevertJobs requests when a newer load is triggered.
  private cancelLoad$ = new Subject<void>();

  public isOpened = false;
  public jobs: BulkJobHistory[] = [];
  public loading = false;

  // Pagination
  public pageSize = 10;
  public currentPage = 1;
  public totalCount = 0;

  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get pages(): number[] {
    const total = this.totalPages;
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const around = [this.currentPage - 1, this.currentPage, this.currentPage + 1].filter(p => p > 1 && p < total);
    const set = new Set([1, ...around, total]);
    return Array.from(set).sort((a, b) => a - b);
  }

  // Filters
  public actorFilter = '';
  public selectedStatuses: { text: string; value: string }[] = [];
  private prevSelectedStatuses: { text: string; value: string }[] = [];
  public tagMapper = (tags: any[]): any[] => {
    return tags.filter((t) => t.value !== 'ALL');
  };
  public statusOptions = [
    { text: 'All statuses', value: FILTER_STATUS.ALL },
    { text: 'Applied', value: FILTER_STATUS.COMPLETED },
    { text: 'Applying', value: FILTER_STATUS.APPLYING },
    { text: 'Failed', value: FILTER_STATUS.FAILED },
    { text: 'Reverted', value: FILTER_STATUS.REVERTED },
    { text: 'Reverting', value: FILTER_STATUS.REVERTING },
  ];
  public range: {
    type: DateRangePeriod;
    start: Date | null;
    end: Date | null;
  } = {
    type: DateRangePeriod.LAST_90_DAYS,
    start: subDays(startOfToday(), 90),
    end: startOfToday(),
  };

  private actorSearch$ = new Subject<string>();

  constructor(
    private elRef: ElementRef,
    private recommendationService: RecommendationsService,
    private companyService: CompanyService,
    private snotifyService: SnotifyService,
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super();
  }

  ngOnInit(): void {
    this.actorSearch$
      .pipe(debounceTime(350), distinctUntilChanged(), this.autoCleanUp())
      .subscribe(() => {
        this.currentPage = 1;
        this.loadJobs();
      });

    // Restore drawer/modal state from URL on initial page load (e.g. email deep-link).
    // State is set directly (no navigate) because the URL already has the correct params.
    // Loading is gated on getCurrentCompany() — the SessionInterceptor reads currentCompany()
    // synchronously when each HTTP request fires. If the company hasn't loaded yet it sends
    // COMPANY-KEY: NONE and the backend returns 400. ReplaySubject(1) emits immediately when
    // the company is already cached, or waits for the first emission otherwise.
    this.route.queryParams
      .pipe(
        first(),
        switchMap((params) => {
          if (!params['jobBatchId'] && !params['showHistory']) {
            return EMPTY;
          }
          this.isOpened = true;
          document.body.style.overflow = 'hidden';
          return this.companyService.getCurrentCompany().pipe(
            first(),
            map(() => params),
          );
        }),
        this.autoCleanUp(),
      )
      .subscribe((params) => {
        this.loadJobs();
        if (params['jobBatchId']) {
          this.openJobByBatchId(params['jobBatchId']);
        }
      });
  }

  @HostListener('document:keydown.escape')
  public onEscape(): void {
    if (this.isOpened) {
      this.closeDrawer();
    }
  }

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: MouseEvent): void {
    if (!this.isOpened) return;
    // When a detail modal is open, the drawer must not react to outside clicks.
    // The drawer z-index sits above the modal backdrop, so backdrop clicks would
    // otherwise close the drawer unexpectedly while the modal is still visible.
    if (this.modalService.hasOpenModals()) return;
    const target = event.target as HTMLElement;
    // If the element was removed from the DOM by a re-render (e.g. pagination click
    // triggering *ngFor rebuild), ignore the event to avoid closing the drawer.
    if (!document.contains(target)) return;
    const clickedInside = this.elRef.nativeElement.contains(target);
    if (clickedInside) return;
    const isPopup = !!target.closest(
      '.k-animation-container, .k-popup, .k-overlay, .k-list-container, .swal2-container, .modal, .modal-backdrop',
    );
    if (isPopup) return;
    this.closeDrawer();
  }

  public openDrawer(): void {
    this.isOpened = true;
    document.body.style.overflow = 'hidden';
    this.loadJobs();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { showHistory: 'true' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public closeDrawer(): void {
    this.isOpened = false;
    document.body.style.overflow = '';
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { showHistory: null, jobBatchId: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  public onStatusChange(): void {
    this.loadJobs();
  }

  public onDateRangeChange(range: any): void {
    this.range = range;
    this.currentPage = 1;
    this.loadJobs();
  }

  public onActorFilterChange(): void {
    this.actorSearch$.next(this.actorFilter);
  }

  public goToPage(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadJobs();
  }

  public applyFilters(): void {
    this.currentPage = 1;
    this.loadJobs();
  }

  public isLatest(job: BulkJobHistory): boolean {
    const completed = this.jobs.filter(j => !j.isRevert && j.status === 'COMPLETED');
    return completed.length > 0 && completed[0].jobBatchId === job.jobBatchId;
  }

  public isJobReverted(job: BulkJobHistory): boolean {
    return !!job.hasBeenReverted;
  }

  public isJobReverting(job: BulkJobHistory): boolean {
    return !!job.isBeingReverted;
  }

  public isPendingRevert(job: BulkJobHistory): boolean {
    return !!job.pendingRevert;
  }

  public canRevert(job: BulkJobHistory): boolean {
    return (
      !job.isRevert &&
      !this.isJobReverted(job) &&
      !this.isJobReverting(job) &&
      !this.isPendingRevert(job) &&
      ['COMPLETED', 'IN_PROGRESS', 'PENDING', 'FAILED'].includes(job.status)
    );
  }

  public canRetryRevert(job: BulkJobHistory): boolean {
    return job.isRevert && job.status === 'FAILED' && !!job.originalJobBatchId;
  }

  public openDetailModal(job: BulkJobHistory, skipUrlUpdate = false): void {
    // Guard against duplicate modals: the drawer z-index is above the modal backdrop,
    // so job rows remain clickable while a modal is already open.
    if (this.modalService.hasOpenModals()) return;
    // skipUrlUpdate is true when called from the deep-link restore path on page reload:
    // the URL already has the correct params so navigating again would create a second
    // concurrent navigation that conflicts with the ongoing initial Angular navigation.
    if (!skipUrlUpdate) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { showHistory: 'true', jobBatchId: job.jobBatchId },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    const ref = this.modalService.open(BulkHistoryDetailModalComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      windowClass: 'bulk-history-detail-modal-window',
    });
    ref.componentInstance.job = job;
    ref.componentInstance.isReverted = this.isJobReverted(job);

    const clearJobParam = () => this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { jobBatchId: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });

    // Reload the list whenever the modal closes via a revert action so the drawer reflects
    // the updated status (pendingRevert badge, Reverted badge, or IN_PROGRESS job).
    ref.result.then((reason: any) => {
      clearJobParam();
      if (reason === 'reverted' || reason === 'revert-scheduled') {
        this.loadJobs();
      } else if (reason?.navigateTo) {
        // User clicked a related job ID inside the modal — open that job's detail.
        this.openJobByBatchId(reason.navigateTo, false);
      }
    }).catch(() => clearJobParam());
  }

  public onRevert(event: MouseEvent, job: BulkJobHistory): void {
    event.stopPropagation();
    const targetJobBatchId = this.canRetryRevert(job) ? job.originalJobBatchId : job.jobBatchId;
    if (!targetJobBatchId) return;

    const isRetryRevert = this.canRetryRevert(job);
    const isDeferred = job.status === 'IN_PROGRESS' || job.status === 'PENDING';
    const isFailed = job.status === 'FAILED';
    Swal.fire({
      title: isRetryRevert ? 'Retry Revert?' : 'Revert Prices?',
      html: isRetryRevert
        ? `<p>This will queue a new revert job from the original price update.</p>
           <p><strong>${job.totalCount} SKU${job.totalCount === 1 ? '' : 's'}</strong> will be retried.</p>`
        : isFailed
        ? `<p>This failed job may have changed some prices before it stopped. This will queue a revert for any SKU that was successfully applied.</p>
           <p><strong>${job.doneCount || job.totalCount} SKU${(job.doneCount || job.totalCount) === 1 ? '' : 's'}</strong> may be affected.</p>`
        : isDeferred
          ? `<p>This will <strong>schedule</strong> a revert — prices will be restored automatically once the job finishes applying.</p>
             <p><strong>${job.totalCount} SKU${job.totalCount === 1 ? '' : 's'}</strong> will be affected.</p>`
          : `<p>This will restore the prices for all SKUs back to what they were before.</p>
             <p><strong>${job.totalCount} SKU${job.totalCount === 1 ? '' : 's'}</strong> will be affected.</p>`,
      type: 'warning',
      showCloseButton: true,
      showCancelButton: true,
      confirmButtonText: isRetryRevert
        ? `Retry revert for ${job.totalCount} SKU${job.totalCount === 1 ? '' : 's'}`
        : isDeferred
        ? `Schedule revert for ${job.totalCount} SKU${job.totalCount === 1 ? '' : 's'}`
        : `Yes, revert ${isFailed ? 'applied' : job.totalCount} SKU${!isFailed && job.totalCount === 1 ? '' : 's'}`,
      confirmButtonClass: 'btn btn-danger',
      cancelButtonClass: 'btn btn-secondary',
      allowOutsideClick: false,
      onBeforeOpen: (popup: HTMLElement) => {
        const container = popup.closest('.swal2-container') as HTMLElement;
        if (container) container.style.zIndex = '99999';
      },
    }).then((result) => {
      if (!result.dismiss) {
        this.recommendationService
          .revertBulkJob(targetJobBatchId)
          .pipe(this.autoCleanUp())
          .subscribe({
            next: (res) => {
              if (res?.scheduled) {
                this.snotifyService.info(
                  'Revert scheduled — prices will restore automatically once the job completes.',
                  'Revert Scheduled',
                  { timeout: 6000 }
                );
                const target = this.jobs.find(j => j.jobBatchId === job.jobBatchId);
                if (target) target.pendingRevert = true;
              } else {
                this.snotifyService.success(
                  isRetryRevert
                    ? 'Revert retry has been queued. Prices will be restored shortly.'
                    : 'Revert job has been queued. Prices will be restored to their previous values shortly.',
                  isRetryRevert ? 'Revert Retry Queued' : 'Revert Queued',
                  { timeout: 5000 }
                );
                const target = this.jobs.find(j => j.jobBatchId === job.jobBatchId);
                if (target) target.isBeingReverted = true;
              }
              this.loadJobs();
            },
            error: () => {
              this.snotifyService.error('Failed to queue revert job', 'Revert Failed', { timeout: 5000 });
            },
          });
      }
    });
  }

  private buildStatusParams(): Record<string, any>[] {
    return this.selectedStatuses.map((statusOption) => {
      switch (statusOption.value) {
        case FILTER_STATUS.REVERTED:
          return { status: FILTER_STATUS.COMPLETED, isRevert: true };
        case FILTER_STATUS.REVERTING:
          return {
            statuses: [FILTER_STATUS.IN_PROGRESS, FILTER_STATUS.PENDING],
            isRevert: true,
          };
        case FILTER_STATUS.APPLYING:
          return {
            statuses: [FILTER_STATUS.IN_PROGRESS, FILTER_STATUS.PENDING],
            isRevert: false,
          };
        default:
          return { status: statusOption.value, isRevert: false };
      }
    });
  }

  private buildDateParams(): Record<string, string> {
    const params: Record<string, string> = {};
    if (this.range.start) {
      params.startDate = new Date(this.range.start).toISOString();
    }
    if (this.range.end) {
      const end = new Date(this.range.end);
      end.setHours(23, 59, 59, 999);
      params.endDate = end.toISOString();
    }
    return params;
  }
  private handleToggleAll(): void {
    const hasAll = this.selectedStatuses.some(
      (item) => item.value === 'ALL',
    );
    const wasAllSelected = this.prevSelectedStatuses.some(
      (item) => item.value === 'ALL',
    );
    // 1. Handle Toggle Logic
    if (hasAll && !wasAllSelected) {
      // Toggle ON: Select everything (KEEP 'ALL' in the array so it stays checked!)
      this.selectedStatuses = [...this.statusOptions];
      this.prevSelectedStatuses = [...this.selectedStatuses];
      return;
    }
    if (!hasAll && wasAllSelected) {
      // Toggle OFF: Clear everything
      this.selectedStatuses = [];
      this.prevSelectedStatuses = [];
      return;
    }
    if (hasAll && this.selectedStatuses.length < this.statusOptions.length) {
      // If a single item is unchecked while ALL was on, remove the ALL check
      this.selectedStatuses = this.selectedStatuses.filter(
        (t) => t.value !== 'ALL',
      );
    }
    // Sync the tracker for the next interaction
    this.prevSelectedStatuses = [...this.selectedStatuses];
  }

  private loadJobs(): void {
    // Cancel any previous in-flight request before starting a new one.
    this.cancelLoad$.next();
    this.loading = true;
    this.handleToggleAll();

    const hasAllStatuses = this.selectedStatuses.some(
      (item) => item.value === FILTER_STATUS.ALL,
    );
    const statusFilters = hasAllStatuses ? [] : this.buildStatusParams();

    const params: any = {
      offset: (this.currentPage - 1) * this.pageSize,
      limit: this.pageSize,
      statusFilters: statusFilters,
      ...this.buildStatusParams(),
      ...this.buildDateParams(),
    };
    if (this.actorFilter.trim()) {
      params.actor = this.actorFilter.trim();
    }

    this.recommendationService
      .getBulkJobHistory(params)
      .pipe(takeUntil(this.cancelLoad$), this.autoCleanUp())
      .subscribe({
        next: (res) => {
          const items = res?.data ?? [];
          this.totalCount = res?.total ?? items.length;
          this.jobs = items.map((j: BulkJobHistory) => ({
            ...j,
            actorLabel: j.triggeredByUserId || j.triggeredBySystem || 'System',
          }));
          this.loading = false;
        },
        error: () => {
          this.snotifyService.error('Failed to load bulk upload history');
          this.loading = false;
        },
      });
  }

  private openJobByBatchId(jobBatchId: string, skipUrlUpdate = true): void {
    this.recommendationService
      .getBulkJobById(jobBatchId)
      .pipe(first(), this.autoCleanUp())
      .subscribe({
        next: (job) => {
          if (!job) return;
          this.openDetailModal(
            { ...job, actorLabel: job.triggeredByUserId || job.triggeredBySystem || 'System' },
            skipUrlUpdate,
          );
        },
        error: () => this.snotifyService.error('Failed to load job details'),
      });
  }
}
